import React, {
  ReactComponentElement,
  ReactElement,
  useEffect,
  useState,
} from "react";
import { DialogBox, DialogContainer } from "../../Atoms/BalanceWarning/styles";
import { DialogActions, DialogTitle, Grid, TextField, Typography, InputAdornment } from "@mui/material";
import Tooltip from "../../Atoms/Tooltip";

import imageMasterToPv from "../../../Assets/manage_balance.jpg";
import imagePvToMaster from "../../../Assets/manage_balance2.jpg";
import { useToast } from "../../../Hooks/toast";
import { Button, ConfirmDialogModal } from "../../Atoms";
import { OrganizerCardDetail } from "../../../Pages/Private/Organizers/styles";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  formatCurrency,
  simpleConvertToCents,
} from "../../../Utils/formatters";
import {
  formatInputValue,
  maskToDecimal,
} from "../../../Utils/formatInputValue";
import { useAccountBalanceData } from "../../../Hooks";
import { Error } from "../../Atoms/Input/styles";
import {
  useSubAccountAmountAdd,
  useSubAccountAmountWithdraw,
} from "../../../Hooks/useSubAccounts";
import { useHistory } from "react-router-dom";

type MoneyType = "add-amount" | "withdraw-amount" | "";

interface IManageBalanceModalProps {
  pointSaleName: string;
  children: any;
  pointSaleAmount: number;
  subAccountId: string;
}

// dispatch fresh events to ensure listeners are triggered reliably

const useManageBalanceModal = ({ subAccountId, pointSaleAmount }) => {
  const { addToast } = useToast();

  const [amount, handleAmount] = useState("");
  const [moneyType, handleMoneyType] = useState<MoneyType>("");

  const { balanceValue, handleGET: fetchAccountBalanceGET } =
    useAccountBalanceData();

  const {
    handlePOST: addAmountSubAccount,
    success: successAddAmountSubAccount,
    loading: loadingAddAmountSubAccount,
  } = useSubAccountAmountAdd();

  const {
    handlePOST: withdrawAmountSubAccount,
    success: successWithdrawAmountSubAccount,
    loading: loadingWithdrawAmountSubAccount,
  } = useSubAccountAmountWithdraw();

  const handleOpenModal = (moneyType: MoneyType) => {
    handleMoneyType(moneyType);
  };

  const handleCloseModal = () => {
    handleMoneyType("");
    handleAmount("");
  };

  const open = Boolean(moneyType);

  const moneyTypeLabel = {
    "add-amount": "Adicionar valor",
    "withdraw-amount": "Resgatar valor",
  };

  const isErrorAdd = simpleConvertToCents(amount) > balanceValue;
  const isErrorWithDraw = simpleConvertToCents(amount) > pointSaleAmount;

  const onSubmit = () => {
    //Caso o valor do input seja 0 ou invalido! nao fazer request
    if (!simpleConvertToCents(amount)) return;
    if (moneyType === "add-amount") {
      try {
        addAmountSubAccount({
          id: subAccountId,
          amount: Number(simpleConvertToCents(amount)),
        });
      } catch (error) {
        console.log(error);
      }
    }
    if (moneyType === "withdraw-amount") {
      try {
        withdrawAmountSubAccount({
          id: subAccountId,
          amount: Number(simpleConvertToCents(amount)),
        });
      } catch (error) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    fetchAccountBalanceGET();
  }, []);

  useEffect(() => {
    if (!isErrorAdd && successAddAmountSubAccount.POST) {
      fetchAccountBalanceGET();
      addToast({
        title: "Successo",
        description: `O valor de ${amount} foi transferido com sucesso para o ponto de venda.`,
        type: "success",
      });
      // history.replace(`/pontos-de-venda?ponto=${subAccountId}`);
      window.dispatchEvent(new Event("refresh-balance"));
      handleCloseModal();
    }
  }, [successAddAmountSubAccount.POST]);

  useEffect(() => {
    if (!isErrorWithDraw && successWithdrawAmountSubAccount.POST) {
      fetchAccountBalanceGET();
      addToast({
        title: "Successo",
        description: `O valor de ${amount} foi resgatado com sucesso para sua conta principal.`,
        type: "success",
      });
      // history.replace(`/pontos-de-venda?ponto=${subAccountId}`);
      window.dispatchEvent(new Event("refresh-balance"));
      handleCloseModal();
    }
  }, [successWithdrawAmountSubAccount.POST]);

  return {
    open,
    moneyType,
    moneyTypeLabel,
    amount,
    isErrorAdd,
    isErrorWithDraw,
    balanceValue,
    loadingAddAmountSubAccount,
    loadingWithdrawAmountSubAccount,
    handleAmount,
    onSubmit,
    handleCloseModal,
    handleOpenModal,
    fetchAccountBalanceGET,
  };
};

const ManageBalanceModal = (props: IManageBalanceModalProps) => {
  const { pointSaleName, pointSaleAmount, children, subAccountId } = props;
  const {
    open,
    moneyType,
    amount,
    moneyTypeLabel,
    balanceValue,
    isErrorAdd,
    isErrorWithDraw,
    loadingAddAmountSubAccount,
    loadingWithdrawAmountSubAccount,
    handleCloseModal,
    handleAmount,
    handleOpenModal,
    onSubmit,
    fetchAccountBalanceGET,
  } = useManageBalanceModal({ subAccountId, pointSaleAmount });
  const [openConfirm, setOpenConfirm] = useState(false);
  const isInputError =
    (moneyType === "add-amount" && isErrorAdd) ||
    (moneyType === "withdraw-amount" && isErrorWithDraw);

  const amountCents = Number(simpleConvertToCents(amount));
  const isAdd = moneyType === "add-amount";
  const mainFrom = balanceValue || 0;
  const posFrom = pointSaleAmount || 0;
  const mainTo = isAdd ? mainFrom - amountCents : mainFrom + amountCents;
  const posTo = isAdd ? posFrom + amountCents : posFrom - amountCents;

  const greaterColor = "#1b5e20";
  const lesserColor = "#b71c1c";
  const colorByComparison = (a: number, b: number) => (a > b ? greaterColor : a < b ? lesserColor : undefined);

  const handleOpenConfirm = () => {
    if (!amountCents || isInputError || !moneyType) return;
    setOpenConfirm(true);
  };

  const handleConfirmAndSubmit = () => {
    setOpenConfirm(false);
    onSubmit();
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
    // Recarrega o saldo local e dispara atualização global
    fetchAccountBalanceGET();
    window.dispatchEvent(new Event("refresh-balance"));
  };
  return (
    <>
      <DialogContainer open={open} onClose={handleCloseModal}>
        <DialogTitle>
          {moneyType === "add-amount" ? (
            <AddBusinessIcon />
          ) : moneyType === "withdraw-amount" ? (
            <PointOfSaleIcon />
          ) : null}
          {moneyTypeLabel[moneyType]} - <strong>{pointSaleName}</strong>
        </DialogTitle>
        <DialogBox>
          <Grid
            container
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            xs={12}
            sx={{ padding: 2 }}
            spacing={2}
          >
            <Grid item xs={12} justifyContent="center">
              <Typography variant="body1">
                <InfoOutlinedIcon style={{ verticalAlign: "middle", marginRight: 6 }} />
                {moneyType === "withdraw-amount"
                  ? "Escolha o quanto deseja resgatar do ponto de venda selecionado para sua conta principal."
                  : "Escolha o quanto deseja transferir de sua conta principal para o ponto de venda selecionado."}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <img
                src={
                  moneyType === "withdraw-amount" ? imagePvToMaster : imageMasterToPv
                }
                width={300}
                alt={
                  moneyType === "withdraw-amount"
                    ? "Transferir do ponto de venda para a conta principal"
                    : "Transferir da conta principal para o ponto de venda"
                }
              />
            </Grid>
            <Grid item xs={12} justifyContent="center">
              <Typography>
                Após inserir a quantia desejada clique em
                <strong> {moneyTypeLabel[moneyType]}</strong>
              </Typography>
            </Grid>
            <Grid item xs={12} justifyContent="center">
              <OrganizerCardDetail>
                <Grid
                  container
                  xs={12}
                  justifyContent="center"
                  sx={{ padding: 1 }}
                  flexDirection="column"
                  spacing={2}
                >
                  {moneyType === "add-amount" && (
                    <>
                      <Grid item>
                        <strong>
                          Saldo principal: {formatCurrency(balanceValue)}
                        </strong>
                      </Grid>
                      <Grid item>
                        <strong style={{ fontSize: 12 }}>
                          Saldo do ponto de venda:{" "}
                          {formatCurrency(pointSaleAmount)}
                        </strong>
                      </Grid>
                    </>
                  )}
                  {moneyType === "withdraw-amount" && (
                    <>
                      <Grid item>
                        <strong>
                          Saldo do ponto de venda:
                          {formatCurrency(pointSaleAmount)}
                        </strong>
                      </Grid>
                      <Grid item>
                        <strong style={{ fontSize: 12 }}>
                          Saldo principal: {formatCurrency(balanceValue)}
                        </strong>
                      </Grid>
                    </>
                  )}
                  <Grid item>
                    <TextField
                      label={`Transferir`}
                      value={amount}
                      error={isInputError}
                      onChange={(e) =>
                        handleAmount(formatInputValue(e.target.value))
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">R$</InputAdornment>
                        ),
                      }}
                      sx={{ width: 400 }}
                    />
                  </Grid>
                  {moneyType === "add-amount" && isErrorAdd && (
                    <Grid item>
                      <Error style={{ color: "red" }}>
                        O saldo desejado é maior que o valor na sua conta
                        principal.
                        <br />
                        Saldo principal:
                        <strong> {formatCurrency(balanceValue)}</strong>
                      </Error>
                    </Grid>
                  )}
                  {moneyType === "withdraw-amount" && isErrorWithDraw && (
                    <Grid item>
                      <Error style={{ color: "red" }}>
                        O saldo desejado é maior que o valor no seu ponto de
                        venda.
                        <br />
                        Saldo ponto de venda:
                        <strong> {formatCurrency(pointSaleAmount)}</strong>
                      </Error>
                    </Grid>
                  )}
                </Grid>
              </OrganizerCardDetail>
            </Grid>
          </Grid>
        </DialogBox>
        <DialogActions>
          <Tooltip title="Fechar">
            <span>
              <Button action="return" onClick={handleCloseModal}>
                <CloseIcon sx={{ fontSize: 20 }} />
                Fechar
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              moneyType === "add-amount"
                ? "Transferir para o ponto de venda"
                : "Resgatar para a conta principal"
            }
          >
            <span>
              <Button
                onClick={handleOpenConfirm}
                loading={
                  loadingAddAmountSubAccount.POST ||
                  loadingWithdrawAmountSubAccount.POST
                }
                disabled={
                  loadingAddAmountSubAccount.POST ||
                  loadingWithdrawAmountSubAccount.POST
                }
              >
                {moneyType === "add-amount" ? (
                  <AddBusinessIcon sx={{ fontSize: 20 }} />
                ) : moneyType === "withdraw-amount" ? (
                  <PointOfSaleIcon sx={{ fontSize: 20 }} />
                ) : null}
                {moneyTypeLabel[moneyType]}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </DialogContainer>
      <ConfirmDialogModal
        open={openConfirm}
        onClose={handleCloseConfirm}
        text={moneyTypeLabel[moneyType] || "Confirmar"}
        description={
          <div>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Saldo principal
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              de
              <strong style={{ marginLeft: 6, color: colorByComparison(mainFrom, mainTo) }}>
                {formatCurrency(mainFrom)}
              </strong>
              <span style={{ margin: "0 6px" }}>→</span>
              <strong style={{ color: colorByComparison(mainTo, mainFrom) }}>
                {formatCurrency(mainTo)}
              </strong>
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Ponto de venda
            </Typography>
            <Typography variant="body1">
              de
              <strong style={{ marginLeft: 6, color: colorByComparison(posFrom, posTo) }}>
                {formatCurrency(posFrom)}
              </strong>
              <span style={{ margin: "0 6px" }}>→</span>
              <strong style={{ color: colorByComparison(posTo, posFrom) }}>
                {formatCurrency(posTo)}
              </strong>
            </Typography>
          </div>
        }
        refreshAction={handleConfirmAndSubmit}
      />
      {children({ handleOpenModal })}
    </>
  );
};

export default ManageBalanceModal;
